import { Button } from './ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { useLocale, type Locale } from '../contexts/LocaleContext'

const languages: { code: Locale; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
]

/**
 * The flag of a UI language, drawn as an SVG. Windows has no flag emoji: it
 * prints 🇫🇷 as the two letters "FR" (issue #1595), so an emoji flag reads as
 * an initial there. The colours are the flags' own, not theme tokens.
 */
export function LanguageFlag({ code, className = 'h-3.5 w-5' }: { code: Locale; className?: string }) {
  if (code === 'fr') {
    return (
      <svg viewBox="0 0 3 2" className={`${className} rounded-[2px]`} aria-hidden="true" data-testid="language-flag-fr">
        <rect width="1" height="2" x="0" fill="#002395" />
        <rect width="1" height="2" x="1" fill="#ffffff" />
        <rect width="1" height="2" x="2" fill="#ed2939" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 19 10" className={`${className} rounded-[2px]`} aria-hidden="true" data-testid="language-flag-en">
      <rect width="19" height="10" fill="#ffffff" />
      {[0, 2, 4, 6, 8, 10, 12].map((i) => (
        <rect key={i} y={(i * 10) / 13} width="19" height={10 / 13} fill="#b22234" />
      ))}
      <rect width="7.6" height={(7 * 10) / 13} fill="#3c3b6e" />
    </svg>
  )
}

export function LanguageToggle() {
  const { locale, setLocale } = useLocale()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-9 h-9 px-0"
          data-testid="language-toggle"
        >
          <LanguageFlag code={locale} />
          <span className="sr-only">Change language</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => setLocale(lang.code)}
            className={locale === lang.code ? 'bg-accent text-accent-foreground' : ''}
            data-testid={`language-${lang.code}`}
          >
            <LanguageFlag code={lang.code} className="mr-2 h-3.5 w-5" />
            <span>{lang.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
